import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI, levelAPI, organizationRoleAPI, departmentAPI } from '../../api/services';

interface Level {
  _id: string;
  name: string;
  code: string;
  rank: number;
  description?: string;
}

interface OrganizationRole {
  _id: string;
  name: string;
  code: string;
  priority: number;
  description?: string;
}

interface Department {
  _id: string;
  name: string;
  code: string;
}

const CreateUser: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [levelId, setLevelId] = useState('');
  const [organizationRoleId, setOrganizationRoleId] = useState('');
  const [primaryDepartmentId, setPrimaryDepartmentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Dynamic data
  const [levels, setLevels] = useState<Level[]>([]);
  const [roles, setRoles] = useState<OrganizationRole[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setDataLoading(true);
      const [levelsRes, rolesRes, deptsRes] = await Promise.all([
        levelAPI.getAllLevels(),
        organizationRoleAPI.getAllRoles(),
        departmentAPI.getAllDepartments(),
      ]);

      const levelsData = levelsRes.data.data || [];
      const rolesData = rolesRes.data.data || [];
      const deptsData = deptsRes.data.data || [];

      setLevels(levelsData);
      setRoles(rolesData);
      setDepartments(deptsData);

      // Set defaults
      const defaultLevel = levelsData[0];
      if (defaultLevel) setLevelId(defaultLevel._id);

      const employeeRole = rolesData.find((r: OrganizationRole) => r.code === 'EMPLOYEE');
      if (employeeRole) setOrganizationRoleId(employeeRole._id);
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load form data. Please refresh the page.');
    } finally {
      setDataLoading(false);
    }
  };

  const parseApiError = (msg: string): string => {
    if (!msg) return msg;
    if (msg.includes('E11000') || msg.toLowerCase().includes('duplicate key')) {
      return 'This email is already registered.';
    }
    return msg;
  };

  const validateCreate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = 'Name is required.';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters.';
    }
    if (!email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Enter a valid email address.';
    }
    if (!levelId) {
      newErrors.levelId = 'Level is required.';
    }
    if (!organizationRoleId) {
      newErrors.organizationRoleId = 'Organization role is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateCreate()) return;
    setLoading(true);
    setError('');

    try {
      // Find the level code for legacy support
      const selectedLevel = levels.find(l => l._id === levelId);

      await userAPI.createUser({
        name,
        email,
        level: selectedLevel?.code || 'L0',
        levelId,
        organizationRoleId,
        primaryDepartmentId: primaryDepartmentId || undefined,
      } as any);

      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/users');
      }, 2000);
    } catch (err: any) {
      const raw = err.response?.data?.message || 'Failed to create user';
      setError(parseApiError(raw));
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gray-100 py-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Create New User</h2>
            <button
              onClick={() => navigate('/admin/users')}
              className="text-gray-600 hover:text-gray-800"
            >
              Back to Users
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                User created successfully! Redirecting...
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors((prev) => { const n = { ...prev }; delete n.name; return n; }); }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
              />
              {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
              <p className="text-xs text-gray-500 mt-1">
                The initial password will be set to the user's name
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((prev) => { const n = { ...prev }; delete n.email; return n; }); }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-2">
                Level *
              </label>
              <select
                id="level"
                value={levelId}
                onChange={(e) => { setLevelId(e.target.value); setErrors((prev) => { const n = { ...prev }; delete n.levelId; return n; }); }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.levelId ? 'border-red-400' : 'border-gray-300'}`}
              >
                <option value="">Select Level</option>
                {levels.map((level) => (
                  <option key={level._id} value={level._id}>
                    {level.code} - {level.name}
                    {level.description && ` (${level.description})`}
                  </option>
                ))}
              </select>
              {errors.levelId && <p className="mt-1 text-xs text-red-600">{errors.levelId}</p>}
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                Organization Role *
              </label>
              <select
                id="role"
                value={organizationRoleId}
                onChange={(e) => { setOrganizationRoleId(e.target.value); setErrors((prev) => { const n = { ...prev }; delete n.organizationRoleId; return n; }); }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.organizationRoleId ? 'border-red-400' : 'border-gray-300'}`}
              >
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name} (Priority: {role.priority})
                  </option>
                ))}
              </select>
              {errors.organizationRoleId && <p className="mt-1 text-xs text-red-600">{errors.organizationRoleId}</p>}
              <p className="text-xs text-gray-500 mt-1">
                Higher priority roles have more permissions (e.g., HOD: 50, Employee: 10)
              </p>
            </div>

            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                Primary Department
              </label>
              <select
                id="department"
                value={primaryDepartmentId}
                onChange={(e) => setPrimaryDepartmentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Department (Optional)</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name} ({dept.code})
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={loading || success || !levelId || !organizationRoleId}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition duration-200"
            >
              {loading ? 'Creating User...' : 'Create User'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateUser;
